import { useTranslation } from '@client-app/i18n';
import { useAdminContext } from '@/contexts/AdminContext';
import { Permission, useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { featureService } from '@common/api/featureService';
import { useReferralView } from '../Settings/components/useReferralView';
import {
    ArrowClockwise,
    ArrowRight,
    CaretLeft,
    Check,
    Copy,
    CaretDown,
    Gift,
    Question,
    ShareNetwork,
    X,
} from '@phosphor-icons/react';
import { Button } from '@common/components/Button';
import Modal from '../Settings/components/Modal';
import clsx from 'clsx';
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import styles from './ReferralModal.module.scss';
import { resolveWorkspaceSubscriptionDisplayState } from '@/utils/workspaceSubscriptionDisplayState';
import referralStar from '@/assets/referral-star.png';
import { formatReferralCreditAmount, formatReferralDate, getReferralPaymentProgress, getRemainingReferralCreditAmount, getTotalEarnedReferralCreditAmount } from './referralHistory';

export interface ReferralModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type ReferralModalView = 'resolving' | 'main' | 'history' | 'faq';

const selectReferralLink = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select();
};

export const createLocalizedReferralShareUrl = (shareUrl: string, locale: string) => {
    try {
        const localizedUrl = new URL(shareUrl);
        localizedUrl.searchParams.set('lang', locale.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en');
        return localizedUrl.toString();
    } catch {
        return shareUrl;
    }
};

export const ReferralModal: React.FC<ReferralModalProps> = ({ isOpen, onClose }) => {
    const { t, locale } = useTranslation();
    const { currentWorkspace, permissions, userBilling } = useAuth();
    const { modal } = useAdminContext();
    const { showToast } = useToast();

    const canManageSubscription = permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]);
    const canManage = !permissions.isLoading && (permissions.isOwner || canManageSubscription);
    const subscriptionState = resolveWorkspaceSubscriptionDisplayState(currentWorkspace?.subscription, {
        isTrial: userBilling?.isTrial ?? false,
        status: userBilling?.planText.status ?? 'none',
    });
    const canShowReferral = !permissions.isLoading && subscriptionState === 'not-trial';

    const [view, setView] = useState<ReferralModalView>('resolving');
    const [referralDataActivated, setReferralDataActivated] = useState(isOpen);
    const [isOpeningRefresh, setIsOpeningRefresh] = useState(false);
    const [faqReturnView, setFaqReturnView] = useState<'main' | 'history'>('main');
    const [openFaqItems, setOpenFaqItems] = useState<Record<string, boolean>>({});
    const shouldLoadReferralData = canShowReferral && (isOpen || referralDataActivated);
    const {
        view: referralView, loading: isLoading, error: loadError, action: actionRunning,
        load: loadReferralData, act: runReferralAction,
    } = useReferralView(currentWorkspace?.id, shouldLoadReferralData, featureService,
        t('website.billing.referralLoadFailed'), t('website.billing.referralActionFailed'));
    const [copied, setCopied] = useState(false);
    const copyTimeoutRef = useRef<number | null>(null);
    const wasOpenRef = useRef(false);
    const openingRefreshRef = useRef(false);
    const autoCreateAttemptedWorkspaceRef = useRef<string | null>(null);
    const canViewDetails = referralView?.canViewDetails !== false;
    const canViewInviteProgress = canManage && canViewDetails;
    const referrals = canViewInviteProgress ? referralView?.referrals ?? [] : [];
    const shouldOpenInvitationProgress = canViewInviteProgress && referrals.length > 0;
    const resolvedDefaultView = shouldOpenInvitationProgress ? 'history' : 'main';
    const visibleView = view === 'resolving'
        ? referralView ? resolvedDefaultView : isLoading ? 'resolving' : 'main'
        : view === 'history' && !canViewInviteProgress ? 'main' : view;

    // Revoke both the visible history and its FAQ return target when access changes.
    useEffect(() => {
        if (canViewInviteProgress) return;
        setView(previous => previous === 'history' ? 'main' : previous);
        setFaqReturnView('main');
    }, [canViewInviteProgress]);

    const toggleFaqItem = useCallback((itemKey: string, e: React.SyntheticEvent) => {
        e.preventDefault();
        setOpenFaqItems((prev) => ({
            ...prev,
            [itemKey]: !prev[itemKey],
        }));
    }, []);

    useEffect(() => {
        setView('resolving');
        setFaqReturnView('main');
        setOpenFaqItems({});
        if (isOpen) {
            if (autoCreateAttemptedWorkspaceRef.current !== currentWorkspace?.id) {
                autoCreateAttemptedWorkspaceRef.current = null;
            }
            setReferralDataActivated(true);
            return;
        }
        autoCreateAttemptedWorkspaceRef.current = null;
        setCopied(false);
        if (copyTimeoutRef.current) {
            window.clearTimeout(copyTimeoutRef.current);
            copyTimeoutRef.current = null;
        }
    }, [isOpen, currentWorkspace?.id]);

    useEffect(() => {
        const isOpening = isOpen && canShowReferral && !wasOpenRef.current;
        wasOpenRef.current = isOpen && canShowReferral;
        if (!isOpening || !referralView || isLoading) {
            return;
        }

        openingRefreshRef.current = true;
        setIsOpeningRefresh(true);
        void loadReferralData().finally(() => {
            openingRefreshRef.current = false;
            setIsOpeningRefresh(false);
        });
    }, [canShowReferral, isLoading, isOpen, loadReferralData, referralView]);

    useEffect(() => {
        if (
            !isOpen
            || view !== 'resolving'
            || isLoading
            || isOpeningRefresh
            || openingRefreshRef.current
            || loadError
        ) {
            return;
        }

        setView(resolvedDefaultView);
    }, [isLoading, isOpeningRefresh, isOpen, loadError, resolvedDefaultView, view]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const handleCopy = useCallback(async () => {
        if (!canShowReferral) return;
        const shareUrl = referralView?.shareUrl?.trim();
        if (!shareUrl) return;

        try {
            const localizedShareUrl = createLocalizedReferralShareUrl(shareUrl, locale);
            const shareMessage = t('website.referralModal.shareMessage', { inviteLink: localizedShareUrl });
            await navigator.clipboard.writeText(shareMessage);
            setCopied(true);
            showToast(t('website.billing.referralLinkCopied'), 'success');

            if (copyTimeoutRef.current) {
                window.clearTimeout(copyTimeoutRef.current);
            }
            copyTimeoutRef.current = window.setTimeout(() => {
                setCopied(false);
                copyTimeoutRef.current = null;
            }, 2000);
        } catch (err) {
            console.warn('[ReferralModal] Clipboard copy failed:', err);
            showToast(t('website.billing.referralCopyFailed'), 'error');
        }
    }, [canShowReferral, locale, referralView?.shareUrl, showToast, t]);

    const handleAction = useCallback(async (action: 'create') => {
        if (!canManage || !canShowReferral || !currentWorkspace?.id || actionRunning) return;

        await runReferralAction(action);
    }, [actionRunning, canManage, canShowReferral, currentWorkspace?.id, runReferralAction]);

    const handleReferralRetry = useCallback(() => {
        autoCreateAttemptedWorkspaceRef.current = null;
        void loadReferralData();
    }, [loadReferralData]);

    useEffect(() => {
        const workspaceId = currentWorkspace?.id;
        if (
            !isOpen
            || !workspaceId
            || !canManage
            || isLoading
            || loadError
            || actionRunning
            || !referralView?.eligible
            || referralView.shareUrl
            || autoCreateAttemptedWorkspaceRef.current === workspaceId
        ) {
            return;
        }

        autoCreateAttemptedWorkspaceRef.current = workspaceId;
        void handleAction('create');
    }, [actionRunning, canManage, currentWorkspace?.id, handleAction, isLoading, isOpen, loadError,
        referralView?.eligible, referralView?.shareUrl]);

    const loadReferralDetails = useCallback(() => (
        loadReferralData({ reportRefreshError: true })
    ), [loadReferralData]);

    const handleViewDetails = useCallback(() => {
        if (!canViewInviteProgress) return;
        setView('history');
        void loadReferralDetails();
    }, [canViewInviteProgress, loadReferralDetails]);

    const handleOpenPricing = useCallback(() => {
        if (!canManage || !canShowReferral) return;
        onClose();
        modal.openModal('pricing');
    }, [canManage, canShowReferral, modal, onClose]);

    if (!isOpen || !canShowReferral) return null;
    if (!canManage && referralView?.eligible === false && referralView.reasonCode !== 'PROGRAM_DISABLED') return null;

    const remainingCredit = formatReferralCreditAmount(
        getRemainingReferralCreditAmount(referralView?.summary), referralView?.summary?.currency, locale,
    );
    const totalEarnedCredit = formatReferralCreditAmount(
        getTotalEarnedReferralCreditAmount(referralView?.summary), referralView?.summary?.currency, locale,
    );
    const isEligible = referralView?.eligible ?? false;
    const isReferralProgramDisabled = referralView?.reasonCode === 'PROGRAM_DISABLED';
    const modalTitleKey = visibleView === 'history'
        ? 'website.referralModal.historyTitle'
        : visibleView === 'faq'
            ? 'website.referralModal.faqTitle'
            : 'website.referralModal.title';
    const faqItems = [
        'eligibility',
        'existingUsers',
        'earned',
        'amount',
        'firstPayment',
        'usage',
        'stacking',
    ] as const;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t(modalTitleKey)}
            hideHeader
            hideCloseButton
            noPadding
            contentScrollable={false}
            width={visibleView === 'history' ? 'max-w-[760px]' : 'max-w-[620px]'}
            containerClassName={styles.modal}
            bodyClassName={styles.modalBody}
        >
            <div
                className={clsx(styles.viewContainer, visibleView === 'main' && styles.mainSurface)}
                data-testid="referral-modal"
            >
                {visibleView === 'resolving' ? (
                    <div className={styles.resolvingView} data-testid="referral-modal-resolving">
                        <button
                            type="button"
                            onClick={onClose}
                            className={clsx(styles.historyCloseButton, styles.resolvingCloseButton)}
                            aria-label={t('common.close')}
                            data-testid="referral-modal-close"
                        >
                            <X size={18} />
                        </button>
                        <div className={styles.loadingState} role="status">
                            <ArrowClockwise size={22} className="animate-spin" />
                            <span>{t('website.billing.referralLoading')}</span>
                        </div>
                    </div>
                ) : visibleView === 'main' ? (
                    /* Level 1: Reward and invite link */
                    <>
                        <div className={styles.heroHeader}>
                            <button
                                type="button"
                                onClick={onClose}
                                className={styles.closeButton}
                                aria-label={t('common.close')}
                                data-testid="referral-modal-close"
                            >
                                <X size={18} />
                            </button>

                            <div className={styles.heroLayout}>
                                <div className={styles.heroTextContent}>
                                    <h2 id="referral-modal-title" className={styles.heroTitle}>
                                        <span className={styles.heroIntro} lang={locale}>
                                            {t('website.referralModal.titleLead')}
                                            <span className={styles.heroAccent}>{t('website.referralModal.titleAccent')}</span>
                                            <span className={styles.heroCelebration} aria-hidden="true">🎉</span>
                                        </span>
                                        <span className={styles.heroReward}>{t('website.referralModal.heroReward')}</span>
                                    </h2>
                                </div>
                                <div className={styles.heroVisual} aria-hidden="true">
                                    <img src={referralStar} alt="" width={128} height={128} draggable={false} />
                                </div>
                            </div>
                        </div>

                    <div className={clsx(styles.mainView, 'custom-scrollbar')} data-testid="referral-modal-main-content">
                        <section className={styles.howItWorks} aria-labelledby="referral-steps-title">
                            <h3 id="referral-steps-title" className={styles.sectionLabel}>
                                {t('website.referralModal.howItWorks')}
                            </h3>
                            <ol className={styles.stepsList}>
                                <li className={styles.stepRow}>
                                    <span className={styles.stepNumber} aria-hidden="true">1</span>
                                    <ShareNetwork size={20} aria-hidden="true" />
                                    <p>{t('website.referralModal.step1')}</p>
                                </li>
                                <li className={styles.stepRow}>
                                    <span className={styles.stepNumber} aria-hidden="true">2</span>
                                    <Gift size={20} aria-hidden="true" />
                                    <p>{t('website.referralModal.step3')}</p>
                                </li>
                            </ol>
                        </section>

                        {isLoading && !referralView ? (
                            <div className={styles.loadingState} role="status">
                                <ArrowClockwise size={22} className="animate-spin" />
                                <span>{t('website.billing.referralLoading')}</span>
                            </div>
                        ) : loadError ? (
                            <div className={styles.errorBanner}>
                                <span>{loadError}</span>
                                <Button
                                    variant="outline"
                                    color="primary"
                                    size="1"
                                    onClick={handleReferralRetry}
                                >
                                    {t('common.retry')}
                                </Button>
                            </div>
                        ) : isReferralProgramDisabled ? (
                            <div className={styles.upgradePrompt} role="status">
                                <p className={styles.upgradePromptContent}>
                                    {t('website.billing.referralProgramUnavailable')}
                                </p>
                            </div>
                        ) : !isEligible ? (
                            <div className={styles.upgradePrompt}>
                                <div className={styles.upgradePromptContent}>
                                    <p className="font-medium">
                                        {t('website.referralModal.upgradePrompt')}
                                    </p>
                                </div>
                                {canManage ? <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={handleOpenPricing}
                                    className={styles.upgradeBtn}
                                >
                                    <Gift size={16} weight="bold" />
                                    <span>{t('website.referralModal.upgradeToRefer')}</span>
                                </Button> : null}
                            </div>
                        ) : (
                            <div className={styles.linkSection}>
                                <div className={styles.sectionLabel}>
                                    {t('website.referralModal.shareLinkTitle')}
                                </div>
                                {referralView?.shareUrl ? (
                                    <div className={styles.linkBox}>
                                        <input
                                            type="text"
                                            readOnly
                                            value={referralView.shareUrl}
                                            aria-label={t('website.referralModal.shareLinkTitle')}
                                            onDoubleClick={selectReferralLink}
                                            className={styles.linkDisplay}
                                            data-testid="referral-modal-link-display"
                                        />
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            onClick={() => void handleCopy()}
                                            className={styles.copyBtn}
                                            data-testid="referral-modal-copy-btn"
                                        >
                                            {copied ? <Check size={16} weight="bold" /> : <Copy size={16} />}
                                            <span>
                                                {copied
                                                    ? t('website.referralModal.copied')
                                                    : t('website.billing.referralCopyLink')}
                                            </span>
                                        </Button>
                                    </div>
                                ) : canManage ? (
                                    <div className={styles.loadingState} role="status">
                                        <ArrowClockwise size={22} className="animate-spin" />
                                        <span>{t('website.billing.referralWorking')}</span>
                                    </div>
                                ) : (
                                    <div className={styles.errorBanner}>
                                        <span>{t('website.billing.referralLoadFailed')}</span>
                                        <Button variant="outline" color="primary" size="1" onClick={handleReferralRetry}>
                                            {t('common.retry')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                        <div className={styles.helpFooter} data-testid="referral-modal-help-footer">
                            <button
                                type="button"
                                onClick={() => { setFaqReturnView('main'); setView('faq'); }}
                                className={styles.helpLink}
                                data-testid="referral-modal-view-faq"
                            >
                                <Question size={16} weight="regular" aria-hidden="true" />
                                <span>{t('website.referralModal.viewFaq')}</span>
                            </button>
                            {canViewInviteProgress ? (
                                <button
                                    type="button"
                                    onClick={handleViewDetails}
                                    className={styles.bottomRewardsBtn}
                                    data-testid="referral-modal-view-history"
                                >
                                    <span>{t('website.referralModal.viewHistory')}</span>
                                    <ArrowRight size={16} weight="regular" aria-hidden="true" />
                                </button>
                            ) : null}
                        </div>
                    </>
                ) : visibleView === 'history' ? (
                    /* ===================================================
                     * Level 2: Invitation history with reliable progress states
                     * =================================================== */
                    <div className={styles.historyPageContainer}>
                        {/* Top Bar with Back Arrow + Title + Close */}
                        <div className={styles.historyTopBar}>
                            <div className={styles.historyTopLeft}>
                                <button
                                    type="button"
                                    onClick={() => setView('main')}
                                    className={styles.backBtn}
                                    aria-label={t('website.referralModal.backToInvite')}
                                    data-testid="referral-modal-back-btn"
                                >
                                    <CaretLeft size={20} weight="bold" />
                                </button>
                                <h3 className={styles.historyTitle}>
                                    {t('website.referralModal.historyTitle')}
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className={styles.historyCloseButton}
                                aria-label={t('common.close')}
                                data-testid="referral-modal-close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Table / List View */}
                        <div
                            className={clsx(
                                styles.historyScroll,
                                'custom-scrollbar',
                            )}
                        >
                            {isLoading && referrals.length === 0 ? (
                                <div className={styles.loadingState} role="status">
                                    <ArrowClockwise size={22} className="animate-spin" />
                                    <span>{t('website.billing.referralLoading')}</span>
                                </div>
                            ) : loadError || !referralView ? (
                                <div className={styles.errorBanner} role="alert">
                                    <span>{t('website.billing.referralLoadFailed')}</span>
                                    <Button variant="outline" color="primary" size="1" onClick={() => void loadReferralDetails()}>
                                        {t('common.retry')}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <dl className={styles.creditOverview} aria-label={t('website.referralModal.creditOverview')}>
                                        <div className={styles.primaryMetric} data-testid="referral-remaining-credit">
                                            <dt className={styles.creditBadge}>
                                                {t('website.referralModal.remainingCredit')}
                                            </dt>
                                            <dd aria-label={remainingCredit === null ? t('website.referralModal.creditUnavailable') : undefined}>{remainingCredit ?? '—'}</dd>
                                            <p className={styles.metricNote}>{t('website.referralModal.autoApplyNote')}</p>
                                        </div>
                                    </dl>
                                    <div className={styles.historySummary}>
                                        <div className={styles.historyTotals}>
                                            <p className={styles.historyTotal} data-testid="referral-total-earned">
                                                <span>{t('website.referralModal.totalEarned')}</span>
                                                <span aria-label={totalEarnedCredit === null ? t('website.referralModal.creditUnavailable') : undefined}>
                                                    {totalEarnedCredit ?? '—'}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                            {referrals.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <Gift size={36} className="text-[#A2A9C0] mb-2" />
                                    <h3 className={styles.emptyStateTitle}>{t('website.referralModal.emptyHistoryTitle')}</h3>
                                    <p className={styles.emptyStateDescription}>{t('website.referralModal.emptyHistory')}</p>
                                </div>
                            ) : (
                                <div className={styles.tableWrap}>
                                    <div className={styles.tableHeaderRow}>
                                        <span className={styles.colEmail}>
                                            {t('website.referralModal.tableEmail')}
                                        </span>
                                        <span className={styles.colWorkspace}>
                                            {t('website.referralModal.tableWorkspace')}
                                        </span>
                                        <span className={styles.colStatus}>
                                            {t('website.referralModal.tableStatus')}
                                        </span>
                                        <span className={styles.colCredit}>
                                            {t('website.referralModal.creditEarned')}
                                        </span>
                                        <span className={styles.colDate}>
                                            {t('website.referralModal.tableDate')}
                                        </span>
                                    </div>
                                    <div className={styles.recordsList}>
                                        {referrals.map((referral, index) => {
                                            const referralCredit = referral.credit;
                                            const creditAmount = formatReferralCreditAmount(referralCredit?.originalAmountMinor, referralCredit?.currency, locale);
                                            const latestActivityAt = referralCredit?.settledAt
                                                || referralCredit?.earnedAt
                                                || referral.createdAt;
                                            const latestActivityDate = formatReferralDate(latestActivityAt, locale) ?? '—';
                                            const workspaceName = referral.referredWorkspaceName || '';
                                            const inviteeEmail = referral.referredCreatorEmailMasked || '—';
                                            const progress = getReferralPaymentProgress(referral);
                                            // Historical first-subscription completion, not current plan activity.
                                            // Credit use/reversal does not undo the original subscription event.
                                            const progressLabel = progress === 'pending'
                                                ? t('website.referralModal.notSubscribed')
                                                : progress === 'unknown'
                                                    ? '—'
                                                    : t('website.referralModal.subscribed');
                                            return (
                                                <div
                                                    key={`${workspaceName}-${inviteeEmail}-${index}`}
                                                    className={styles.recordRow}
                                                >
                                                    <div className={styles.colEmail}>
                                                        <span className={styles.cellText}>
                                                            {inviteeEmail}
                                                        </span>
                                                    </div>
                                                    <div className={styles.colWorkspace}>
                                                        <span className={styles.cellText}>
                                                            {workspaceName}
                                                        </span>
                                                    </div>
                                                    <div className={styles.colStatus}>
                                                        <span
                                                            className={progress === 'unknown' ? undefined : clsx(
                                                                styles.statusBadge,
                                                                progress !== 'pending' && styles.statusBadgePaid,
                                                            )}
                                                        >
                                                            {progressLabel}
                                                        </span>
                                                    </div>
                                                    <div className={styles.colCredit} aria-label={creditAmount === null ? t('website.referralModal.creditUnavailable') : undefined}>
                                                        {creditAmount ?? '—'}
                                                    </div>
                                                    <div className={styles.colDate}>
                                                        <span className="text-xs text-[#666F8D]">
                                                            {latestActivityDate}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                                </>
                            )}
                        </div>

                        <div className={styles.historyFooter}>
                            <button type="button" className={styles.helpLink} data-testid="referral-modal-history-faq" onClick={() => { setFaqReturnView('history'); setView('faq'); }}>
                                <Question size={16} weight="regular" aria-hidden="true" />
                                <span>{t('website.referralModal.viewFaq')}</span>
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ===================================================
                     * Level 2: User-facing referral FAQ
                     * =================================================== */
                    <div className={styles.historyPageContainer}>
                        <div className={styles.historyTopBar}>
                            <div className={styles.historyTopLeft}>
                                <button
                                    type="button"
                                    onClick={() => setView(faqReturnView === 'history' && !canViewInviteProgress ? 'main' : faqReturnView)}
                                    className={styles.backBtn}
                                    aria-label={t('website.referralModal.backToInvite')}
                                    data-testid="referral-modal-faq-back-btn"
                                >
                                    <CaretLeft size={20} weight="bold" />
                                </button>
                                <h3 className={styles.historyTitle}>
                                    {t('website.referralModal.faqTitle')}
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className={styles.historyCloseButton}
                                aria-label={t('common.close')}
                                data-testid="referral-modal-close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div
                            className={clsx(
                                styles.faqScroll,
                                'custom-scrollbar',
                            )}
                        >
                            <div className={styles.faqList}>
                                {faqItems.map((item) => {
                                    const isOpen = Boolean(openFaqItems[item]);
                                    return (
                                        <details
                                            key={item}
                                            className={clsx(
                                                styles.faqItem,
                                                isOpen && styles.faqItemOpen,
                                            )}
                                            open
                                        >
                                            <summary
                                                className={styles.faqQuestion}
                                                onClick={(e) => toggleFaqItem(item, e)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        toggleFaqItem(item, e);
                                                    }
                                                }}
                                                aria-expanded={isOpen}
                                            >
                                                <span>{t(`website.referralModal.faq.${item}.question`)}</span>
                                                <CaretDown
                                                    size={16}
                                                    weight="bold"
                                                    className={styles.faqChevron}
                                                />
                                            </summary>
                                            <div className={styles.faqAnswerWrapper}>
                                                <div className={styles.faqAnswerInner}>
                                                    <p className={styles.faqAnswer}>
                                                        {t(`website.referralModal.faq.${item}.answer`)}
                                                    </p>
                                                </div>
                                            </div>
                                        </details>
                                    );
                                })}
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ReferralModal;
